import { Label, Select, TextInput, Spinner } from "flowbite-react";

import Button from "@mui/material/Button";

import { useState, useEffect, useContext } from "react";
import { MultipleInput } from "../components/MultipleInput";
import { HiMail, HiInformationCircle } from "react-icons/hi";
import { VscGistSecret } from "react-icons/vsc";
import { LuCloudCog } from "react-icons/lu";
import axiosInstance from "../utils/axiosInstance";
import FormCard from "../components/FormCard";
import { PageTitle } from "../components/PageTitle";
import { Alert } from "flowbite-react";
import { ProtectedComponent } from "../components/ProtectedComponent";
import { envs } from "../constants";
import { useError } from "../contexts/ErrorContext";
import { UserContext } from "../utils/UserContext";
import { webhooks, webHookStructure } from "../constants";
import { MdBrandingWatermark } from "react-icons/md";
import descriptions from "../utils/environmentDescriptions";

import {
  FaPhoneAlt,
  FaUser,
  FaKey,
  FaLink,
  FaRegAddressCard,
} from "react-icons/fa";

function LabelWithDescription({ value, description }) {
  return (
    <div className="mb-2 flex flex-col">
      <Label value={value} />
      {description && <Label className="font-light">{description}</Label>}
    </div>
  );
}

export function Environment() {
  const [areBrandsLoading, setAreBrandsLoading] = useState(false);
  const { user } = useContext(UserContext);
  const { addInfo, setErrors, setErrorList } = useError();
  const [hookAuths, setHookAuths] = useState(webhooks);
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState(null);

  const [environment, setEnvironment] = useState(null);
  const [hookUrlHeaders, setHookUrlHeaders] = useState([]);
  const [hookCorelations, setHookCorelations] = useState([]);
  const [hookAuthHeaders, setHookAuthHeaders] = useState([]);
  const [formUser, setFormUser] = useState({ name: "", email: "", mobile: "" });
  const [keys, setKeys] = useState({
    v4: { backend: "", frontend: "" },
    v5: { backend: "" },
  });
  const [webhook, setWebhook] = useState(webHookStructure);
  const [redirect, setRedirect] = useState();
  const [downloadAnywayEnabled, setDownloadAnywayEnabled] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [isWebHookTested, setIsWebHookTested] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    setCurrentData(null);
    setDownloadAnywayEnabled(false);
    if (!environment) {
      setErrors(["Please select an environment"]);
      return;
    }
    setIsFormSubmitting(true);
    axiosInstance
      .post(`/v51/environments`, {
        env: environment,
        redirect,
        brandId: brand,
        user: formUser,
        webHook: attachMapsToWebhook(),
      })
      .then((res) => {
        if (!res.data.feedback?.length) {
          handleSucces(res.data.message);
        } else {
          setErrors(res.data.feedback);
          setCurrentData(res.data.message);
          setDownloadAnywayEnabled(true);
        }
      })
      .catch((e) => {
        if (e.response.status === 400) {
          setErrors(e.response.data.errors);
        } else {
          setErrors(["Error creating environment"]);
        }
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  function handleSucces(data) {
    const url = window.URL.createObjectURL(new Blob([JSON.stringify(data)]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "environment.json");
    document.body.appendChild(link);
    link.click();
  }

  function attachMapsToWebhook() {
    return {
      ...webhook,
      urlHeaders: convertListToMap(hookUrlHeaders),
      corelations: convertListToMap(hookCorelations),
      authHeaders: convertListToMap(hookAuthHeaders),
    };
  }

  const convertListToMap = (list) => {
    const map = {};
    list.forEach((obj) => {
      map[obj.key] = obj.value;
    });
    return map;
  };

  useEffect(() => {
    if (environment !== null) return;
    axiosInstance
      .get(`/v51/environments`)
      .then((res) => {
        if (!res.data) {
          return;
        }
        setBrand(res.data.brandId);
        setEnvironment(res.data.env);
        setRedirect(res.data.redirect);
        setFormUser(res.data.user);
        setWebhook(res.data.webHook);
      })
      .catch((e) => {
        setErrors(["Error fetching environments"]);
        console.log(e);
      });

    setAreBrandsLoading(true);
    axiosInstance
      .get(`/v51/companies/${user.companyId}/brands`)
      .then((res) => {
        setBrands(res.data);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setAreBrandsLoading(false);
      });
  }, []);

  const testWebHook = () => {
    setIsWebHookTested(true);
    axiosInstance
      .post(`/v51/environments/webhook`, attachMapsToWebhook())
      .then((res) => {
        if (res.data.success) {
          addInfo(["Webhook test successful"]);
        } else {
          setErrorList(res?.data?.feedback);
        }
      })
      .catch((e) => {
        console.log(e);
        setErrors([e.response.data.message]);
      })
      .finally(() => {
        setIsWebHookTested(false);
      });
  };

  return (
    <ProtectedComponent>
      <div className="mx-auto flex w-full flex-col items-center justify-center bg-transparent px-6 py-8 lg:w-9/12 lg:px-2 lg:py-0">
        <PageTitle title={"Postman Environment Configuration"} />
        <FormCard>
          <div id="brand-input">
            <LabelWithDescription
              value="Select the brand"
              description={descriptions.brand}
            />
            {areBrandsLoading ? (
              <Spinner />
            ) : (
              <>
                <Select
                  value={brand}
                  onChange={(event) => {
                    setBrand(event.target.value);
                  }}
                  id="brand"
                  required
                  icon={MdBrandingWatermark}
                >
                  <option disabled value={null} selected>
                    Select a brand
                  </option>
                  {brands.map((brand, index) => (
                    <option value={brand.id}>{brand.name}</option>
                  ))}
                </Select>
              </>
            )}
          </div>
          <div id="environment-input">
            <LabelWithDescription
              value="Select the environment"
              description={descriptions.environment}
            />
            <Select
              value={environment}
              onChange={(event) => {
                setEnvironment(event.target.value);
              }}
              id="environment"
              required
              icon={LuCloudCog}
            >
              <option selected disabled value={null}>
                Select an environment
              </option>
              {envs.map((env, index) => (
                <option value={env.value}>{env.name}</option>
              ))}
            </Select>
          </div>
          <div className="redirect-url-input">
            <LabelWithDescription
              value="Redirect URL"
              description={descriptions.redirectUrl}
            />
            <TextInput
              value={redirect}
              onChange={(event) => {
                setRedirect(event.target.value);
              }}
              id="redirect"
              type="text"
              sizing="md"
              icon={FaLink}
            />
          </div>

          {/* <h5>V4</h5>
          <div>
            <div className="mb-2 block">
              <Label htmlFor="v4-backend" value="Back-end API KEY" />
            </div>
            <TextInput
              value={keys.v4.backend}
              onChange={(event) => {
                setKeys({
                  ...keys,
                  v4: { ...keys.v4, backend: event.target.value },
                });
              }}
              id="v4-backend"
              type="text"
              sizing="md"
              icon={FaKey}
            />
          </div>
          <div>
            <div className="mb-2 block">
              <Label htmlFor="v4-frontend" value="front-end API KEY" />
            </div>
            <TextInput
              value={keys.v4.frontend}
              onChange={(event) => {
                setKeys({
                  ...keys,
                  v4: { ...keys.v4, frontend: event.target.value },
                });
              }}
              id="v4-frontend"
              type="text"
              sizing="md"
              icon={FaKey}
            />
          </div>
          <h5>V5</h5>
          <div>
            <div className="mb-2 block">
              <Label htmlFor="v5-backend" value="Back-end API KEY" />
            </div>
            <TextInput
              value={keys.v5.backend}
              onChange={(event) => {
                setKeys({
                  ...keys,
                  v5: { ...keys.v5, backend: event.target.value },
                });
              }}
              id="v5-backend"
              type="text"
              sizing="md"
              icon={FaKey}
            />
          </div> */}
          {/* <div class="my-5 flex-grow border-t border-gray-400"></div> */}
        </FormCard>
        <FormCard>
          <h5>User details</h5>
          <div id="user-name-input">
            <LabelWithDescription
              value="Name"
              description={descriptions.userName}
            />
            <TextInput
              value={formUser.name}
              onChange={(event) => {
                setFormUser({
                  ...formUser,
                  name: event.target.value,
                });
              }}
              id="user-name"
              type="text"
              sizing="md"
              icon={FaUser}
            />
          </div>
          <div id="user-email-input">
            <LabelWithDescription
              value="Email"
              description={descriptions.userEmail}
            />
            <TextInput
              value={formUser.email}
              onChange={(event) => {
                setFormUser({
                  ...formUser,
                  email: event.target.value,
                });
              }}
              id="user-email"
              sizing="md"
              type="email"
              icon={HiMail}
              placeholder="name@example.com"
            />
          </div>
          <div id="user-mobile-input">
            <LabelWithDescription
              value="Mobile"
              description={descriptions.userMobile}
            />
            <TextInput
              value={formUser.mobile}
              onChange={(event) => {
                setFormUser({
                  ...formUser,
                  mobile: event.target.value,
                });
              }}
              id="user-mobile"
              type="text"
              sizing="md"
              icon={FaPhoneAlt}
            />
          </div>
          {/* <div class="my-5 flex-grow border-t border-gray-400"></div> */}
        </FormCard>
        <FormCard>
          <div className="flex w-full justify-start space-x-10 align-middle">
            <span className="align-middle">Web Hook</span>
            <Button onClick={testWebHook} variant="contained">
              {isWebHookTested ? <Spinner /> : "Test"}
            </Button>
          </div>
          <div id="web-hook-auth-input">
            <LabelWithDescription
              value="Auth"
              description={descriptions.webHookAuth}
            />
            <Select
              value={webhook.auth}
              onChange={(event) => {
                setWebhook({
                  ...webhook,
                  auth: event.target.value,
                });
              }}
              icon={FaLink}
              id="webHook-auth"
              required
            >
              <option selected disabled value={null}>
                Select an authentication type
              </option>
              {hookAuths.map((hookAuth, index) => (
                <option value={hookAuth.value}>{hookAuth.name}</option>
              ))}
            </Select>
          </div>
          <div id="web-hook-url-input">
            <LabelWithDescription
              value="Url"
              description={descriptions.webHookUrl}
            />
            <TextInput
              value={webhook.url}
              onChange={(event) => {
                setWebhook({
                  ...webhook,
                  url: event.target.value,
                });
              }}
              id="webHook-url"
              type="text"
              sizing="md"
              icon={FaLink}
            />
          </div>
          <div className="my-2">
            <MultipleInput
              entries={hookUrlHeaders}
              setEntries={setHookUrlHeaders}
              name="URL headers"
              description={descriptions.webHookUrlHeaders}
            ></MultipleInput>
          </div>
          {webhook.auth === "basic" && (
            <>
              <div>
                <LabelWithDescription
                  value="Username"
                  description={descriptions.webHookUsername}
                />
                <TextInput
                  value={webhook.username}
                  onChange={(event) => {
                    setWebhook({
                      ...webhook,
                      username: event.target.value,
                    });
                  }}
                  id="webHook-username"
                  type="text"
                  sizing="md"
                  icon={FaRegAddressCard}
                />
              </div>
              <div>
                <LabelWithDescription
                  value="Password"
                  description={descriptions.webHookPassword}
                />
                <TextInput
                  value={webhook.password}
                  onChange={(event) => {
                    setWebhook({
                      ...webhook,
                      password: event.target.value,
                    });
                  }}
                  id="webHook-password"
                  type="text"
                  sizing="md"
                  icon={VscGistSecret}
                />
              </div>
            </>
          )}
          {webhook.auth === "api-key" && (
            <>
              <div>
                <LabelWithDescription
                  value="Identity"
                  description={descriptions.webHookIdentity}
                />
                <TextInput
                  value={webhook.identity}
                  onChange={(event) => {
                    setWebhook({
                      ...webhook,
                      identity: event.target.value,
                    });
                  }}
                  id="webHook-idetity"
                  type="text"
                  sizing="md"
                  icon={FaRegAddressCard}
                />
              </div>
              <div>
                <LabelWithDescription
                  value="Secret"
                  description={descriptions.webHookSecret}
                />
                <TextInput
                  value={webhook.secret}
                  onChange={(event) => {
                    setWebhook({
                      ...webhook,
                      secret: event.target.value,
                    });
                  }}
                  id="webHook-secret"
                  type="text"
                  sizing="md"
                  icon={VscGistSecret}
                />
              </div>
            </>
          )}
          {webhook.auth === "oauth2" && (
            <>
              <div>
                <LabelWithDescription
                  value="OAuth Secret Name"
                  description={descriptions.webHookOauthSecretName}
                />
                <TextInput
                  value={webhook.oauthSecretName}
                  onChange={(event) => {
                    setWebhook({
                      ...webhook,
                      oauthSecretName: event.target.value,
                    });
                  }}
                  id="webHook-idetity"
                  type="text"
                  sizing="md"
                  icon={FaRegAddressCard}
                />
              </div>
              <div>
                <LabelWithDescription
                  value="OAuth Secret"
                  description={descriptions.webHookSecret}
                />
                <TextInput
                  value={webhook.oauthSecret}
                  onChange={(event) => {
                    setWebhook({
                      ...webhook,
                      oauthSecret: event.target.value,
                    });
                  }}
                  id="webHook-oauthSecret"
                  type="text"
                  sizing="md"
                  icon={VscGistSecret}
                />
              </div>
              <div>
                <LabelWithDescription
                  value="OAuth url"
                  description={descriptions.webHookOauthUrl}
                />
                <TextInput
                  value={webhook.authUrl}
                  onChange={(event) => {
                    setWebhook({
                      ...webhook,
                      authUrl: event.target.value,
                    });
                  }}
                  icon={FaLink}
                  id="webHook-authUrl"
                  type="text"
                  sizing="md"
                />
              </div>
              <div>
                <MultipleInput
                  entries={hookAuthHeaders}
                  setEntries={setHookAuthHeaders}
                  name="OAuth Headers"
                  description={descriptions.webHookOauthHeaders}
                ></MultipleInput>
              </div>
            </>
          )}
          <div>
            <MultipleInput
              entries={hookCorelations}
              setEntries={setHookCorelations}
              name="Correlation"
              description={descriptions.webHookCorrelations}
            ></MultipleInput>
          </div>

          <Button onClick={handleSubmit} variant="contained">
            {isFormSubmitting ? <Spinner /> : "Submit"}
          </Button>
        </FormCard>
        {downloadAnywayEnabled && (
          <Button
            onClick={() => {
              handleSucces(currentData);
              setCurrentData(null);
              setDownloadAnywayEnabled(false);
            }}
          >
            Download anyway
          </Button>
        )}
      </div>
    </ProtectedComponent>
  );
}
